import {
  Suspense,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { canAnywhere } from '@hrms/shared';
import { useAuthenticatedSession } from '../../../session/SessionProvider.js';
import { useSetupStatus } from '../panel/useSetupStatus.js';
import { LazyAddYourTeamModal } from './lazyAddYourTeamModal.js';

export interface OpenAddTeamOptions {
  /** Open the modal's Excel/CSV file picker straight away ("Upload Excel sheet"). */
  upload?: boolean;
}

export interface AddTeamModalApi {
  open: (options?: OpenAddTeamOptions) => void;
  isOpen: boolean;
}

const AddTeamModalContext = createContext<AddTeamModalApi | null>(null);

/**
 * Opens the one "Add your team" modal. Null outside `AddTeamModalProvider` (a test rendering a
 * piece on its own); callers then render nothing, or their own modal (WelcomeRedirect).
 */
export function useAddTeamModal(): AddTeamModalApi | null {
  return useContext(AddTeamModalContext);
}

/**
 * Holds the single AddYourTeamModal for the app shell, so Home's activation card, the "Add your
 * first 3 staff" bar and the welcome redirect all open the same one instead of stacking their own.
 * Saving refetches setup-status (useSaveTeam), so the card and bar update as soon as people go in.
 */
export function AddTeamModalProvider({ children }: { children: ReactNode }) {
  const { actor } = useAuthenticatedSession();
  const [openWith, setOpenWith] = useState<OpenAddTeamOptions | null>(null);
  // Shares the setup-status cache; only read for the trial cap while the modal is open.
  const status = useSetupStatus(openWith !== null && canAnywhere(actor, 'org.settings:read'));
  const employeeLimit = status.data?.status === 200 ? status.data.body.employeeLimit : null;

  const open = useCallback((options: OpenAddTeamOptions = {}) => setOpenWith(options), []);
  const value = useMemo(() => ({ open, isOpen: openWith !== null }), [open, openWith]);

  return (
    <AddTeamModalContext.Provider value={value}>
      {children}
      {openWith && (
        <Suspense fallback={null}>
          <LazyAddYourTeamModal
            open
            onClose={() => setOpenWith(null)}
            employeeLimit={employeeLimit ?? null}
            autoOpenUpload={openWith.upload === true}
            // Finishing records the welcome as done, which only someone who may change the
            // organisation's settings can; anyone else just closes after adding.
            completesWelcome={canAnywhere(actor, 'org.settings:update')}
          />
        </Suspense>
      )}
    </AddTeamModalContext.Provider>
  );
}
